import { Search, MessageSquare, LogOut, CheckCircle, XCircle, Loader, WalletCards, Info, Rows3 } from 'lucide-react';
import lbIcon from './favicon.png';
import { useState } from "react";

const handleLogout = () => {
  fetch('/auth/logout', {
    method: 'GET',
    credentials: 'include',
  })
    .then(() => {
      // no need to redirect here if backend redirects on logout
      // maybe reload the page or navigate your frontend differently
      window.location.reload();  // or navigate to frontend login page explicitly
    })
    .catch(err => {
      console.error('Logout failed:', err);
    });
};

window.setIsGeneratingReport = (val) => {
  const spinner = document.getElementById("spinner");
  const loader = document.getElementById("loader");
  if (spinner) {
    spinner.classList.toggle("hidden", !val); // remove hidden if val = true, add hidden if val = false
    
  }
  if (loader) {
    loader.classList.toggle("hidden", !val); // remove hidden if val = true, add hidden if val = false
    
  }

};

window.setIsGeneratingHeatmaps = (val) => {
  const spinner = document.getElementById("heatmap-spinner");
  const loader = document.getElementById("heatmap-loader");
  if (spinner) {
    spinner.classList.toggle("hidden", !val); // remove hidden if val = true, add hidden if val = false
    
  }
  if (loader) {
    loader.classList.toggle("hidden", !val); // remove hidden if val = true, add hidden if val = false
    
  }

};

/*
const TopNav = ({ cursor, setCursor, onGenerateReport, toggleChat, searchText, setSearchText, handleKeyDown, onLogout = handleLogout }) => (
  <div className="bg-white shadow-md px-4 py-2 flex items-center justify-between">
    <div className="flex items-center space-x-3">
      <div className="flex items-center space-x-2">
        <img
          src={lbIcon}
          alt="LandBuilder Logo"
          className="w-12 h-12 object-contain"
        />
      <div className="font-bold text-lg text-blue-600">Acquisition Intel</div>
      </div>
      <div className="relative w-96">
        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search Lat, Long; APN or Full Address"
          className="w-full pl-8 pr-3 py-1 rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <Search className="absolute left-2 top-2 text-gray-400" size={16} />
      </div>
    </div>

    <div className="flex items-center space-x-4">
      <button
        className="flex items-center space-x-1 text-gray-700 hover:text-red-600"
        onClick={onLogout}
        title="Logout"
      >
        <LogOut size={18} />
        <span>Logout</span>
      </button>
    </div>
  </div>
);
*/


const TopNav = ({
  cursor,
  setCursor,
  onGenerateReport,
  toggleChat,
  searchText,
  setSearchText,
  searchError,
  handleKeyDown,
  userName,
  creditSummary,
  portfolioEnabled = false,
  onLogout=handleLogout
}) => {
  const [showStatus, setShowStatus] = useState(false);
  const [reportStatus, setReportStatus] = useState(null);
  const [showCredits, setShowCredits] = useState(false);
  const [creditStatus, setCreditStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [creditsLoading, setCreditsLoading] = useState(false);

  const fetchReportStatus = async () => {
    setStatusLoading(true);
    try {
      const res = await fetch("/api/report-status", { credentials: "include" });
      const data = await res.json();
      setReportStatus(data);
    } catch (err) {
      console.error("Failed to fetch analysis status:", err);
      setReportStatus({ error: "Failed to fetch analysis status" });
    } finally {
      setStatusLoading(false);
    }
  };

  const handleMessageClick = () => {
    setShowStatus(true);
    fetchReportStatus();
    setTimeout(() => setShowStatus(false), 5000);
  };

  const fetchCreditStatus = async () => {
    setCreditsLoading(true);
    try {
      const res = await fetch("/auth/credits", { credentials: "include" });
      const data = await res.json();
      setCreditStatus(data);
    } catch (err) {
      console.error("Failed to fetch credit status:", err);
      setCreditStatus({ error: "Failed to fetch credit status" });
    } finally {
      setCreditsLoading(false);
    }
  };

  const handleCreditClick = () => {
    setShowCredits(true);
    fetchCreditStatus();
    setTimeout(() => setShowCredits(false), 7000);
  };

  const previewCredits = Number((creditStatus?.preview_credits ?? creditSummary?.preview_credits) || 0);
  const analysisCredits = Number((creditStatus?.analysis_credits ?? creditSummary?.analysis_credits) || 0);
  const previewUnlimited = Boolean(creditStatus?.preview_credits_unlimited ?? creditSummary?.preview_credits_unlimited);
  const analysisUnlimited = Boolean(creditStatus?.analysis_credits_unlimited ?? creditSummary?.analysis_credits_unlimited);
  const activePlan = String(creditStatus?.plan || creditSummary?.plan || "native");
  const creditCardPayload = creditStatus || creditSummary || null;
  const statusCardPayload = reportStatus || null;

  const getPreviewTone = (count) => {
    if (previewUnlimited) return "text-emerald-700 bg-emerald-50 border-emerald-200";
    if (count <= 10) return "text-red-700 bg-red-50 border-red-200";
    if (count <= 20) return "text-amber-700 bg-amber-50 border-amber-200";
    return "text-emerald-700 bg-emerald-50 border-emerald-200";
  };

  const getAnalysisTone = (count) => {
    if (analysisUnlimited) return "text-emerald-700 bg-emerald-50 border-emerald-200";
    if (count <= 3) return "text-red-700 bg-red-50 border-red-200";
    if (count <= 6) return "text-amber-700 bg-amber-50 border-amber-200";
    return "text-emerald-700 bg-emerald-50 border-emerald-200";
  };

  return (
    <div className="bg-white shadow-md px-3 py-2 flex flex-wrap items-center gap-2 justify-between relative sm:px-4">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 sm:flex-nowrap sm:space-x-3">
        <div className="flex items-center space-x-2">
          <img
            src={lbIcon}
            alt="LandBuilder Logo"
            className="w-12 h-12 object-contain"
          />
          <div className="hidden font-bold text-lg text-blue-600 sm:block">Acquisition Intel</div>
        </div>
        <div className="relative order-last w-full min-w-0 sm:order-none sm:w-96">
          <div className="flex items-center gap-2">
          <div className="relative flex-1">
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search Lat, Lon; APN or Full Address"
            className="w-full pl-8 pr-3 py-1 rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <Search className="absolute left-2 top-2 text-gray-400" size={16} />
          </div>
          <div className="group static flex items-center">
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition-colors hover:border-blue-300 hover:text-blue-600 focus:outline-none"
              aria-label="Search format help"
            >
              <Info size={14} />
            </button>
            <div className="pointer-events-none absolute left-0 top-full z-50 mt-2 hidden w-[40rem] max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white px-4 py-3 text-xs leading-5 text-gray-600 shadow-xl group-hover:flex">
              <div className="min-w-0 flex-1">
                <span className="font-semibold text-gray-800">Search Examples:</span>{" "}
                <span className="font-medium text-gray-700">Address</span> `99 S 4th St, San Jose, CA`
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <span className="font-medium text-gray-700">APN</span> `28 3703-33-2-13`, `4-24-00-020.00`, `46723096`
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <span className="font-medium text-gray-700">Lat/Lon</span> `27.3670,-82.3717`, `27.3670 -82.3717`, `27.3670; -82.3717`
              </div>
            </div>
          </div>
          </div>
          {searchError && (
            <p className="mt-1 text-xs text-red-600">
              {searchError}
            </p>
          )}
        </div>
      </div>

      <div className="flex w-full flex-wrap items-center justify-between gap-1 sm:w-auto sm:flex-nowrap sm:justify-end sm:gap-4">
        {userName && portfolioEnabled && (
          <a
            href="/portfolio"
            className="flex items-center space-x-1 rounded-lg px-1 py-1.5 font-medium text-gray-700 transition-colors hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:px-3"
          >
            <Rows3 size={18} className="text-blue-600" />
            <span>Land Portfolio</span>
          </a>
        )}
        {userName && (
          <div className="relative">
            <div
              className="flex items-center space-x-2 px-1 py-1.5 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer sm:px-3"
              onClick={handleCreditClick}
            >
              <WalletCards size={18} className="text-blue-600" />
              <span className="font-medium text-gray-700">
                Credit Center
              </span>
            </div>

            {showCredits && (
              <div className="absolute right-0 top-12 w-80 bg-white shadow-xl border border-gray-200 rounded-xl p-4 text-sm z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                {creditCardPayload?.error ? (
                  <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg">
                    <XCircle size={18} />
                    <span>{creditCardPayload.error}</span>
                  </div>
                ) : !creditCardPayload ? (
                  <div className="flex items-center space-x-2 text-gray-600 bg-gray-50 p-3 rounded-lg">
                    <Loader size={18} className="animate-spin text-blue-600" />
                    <span>Loading credit summary...</span>
                  </div>
                ) : (
                  <>
                    <div className="mb-4 flex items-start justify-between gap-3 border-b border-gray-200 pb-3">
                      <div className="font-semibold text-gray-800">
                        Credit Center
                        <div className="text-xs text-gray-500 font-normal mt-1">
                        Latest available credits from your account
                      </div>
                        <div className="mt-2 inline-flex rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                          Plan: {activePlan}
                        </div>
                      </div>
                      {creditsLoading && (
                        <Loader size={16} className="mt-1 animate-spin text-blue-600" />
                      )}
                    </div>

                    <div className="space-y-3">
                      <div
                        className={`rounded-xl border p-3 ${getPreviewTone(previewCredits)}`}
                        title="Preview credits is the number of times you can click on parcel previews."
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-semibold">Preview Credits</div>
                          <div className="text-lg font-bold">{previewUnlimited ? "Unlimited" : previewCredits}</div>
                        </div>
                        <div className="mt-2 text-xs leading-5 opacity-90">
                          Preview credits is the number of times you can click on parcel previews.
                        </div>
                      </div>

                      <div
                        className={`rounded-xl border p-3 ${getAnalysisTone(analysisCredits)}`}
                        title="Analysis credits is the number of times you can click on generate reports. Preview credits are scaled higher than analysis credits."
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-semibold">Analysis Credits</div>
                          <div className="text-lg font-bold">{analysisUnlimited ? "Unlimited" : analysisCredits}</div>
                        </div>
                        <div className="mt-2 text-xs leading-5 opacity-90">
                          Analysis credits is the number of times you can click on generate reports. Preview credits are scaled higher than analysis credits.
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Account info with status popup */}
        {userName && (
          <div className="relative">
            <div
              className="flex items-center space-x-2 px-1 py-1.5 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer sm:px-3"
              onClick={handleMessageClick}
            >
              <MessageSquare size={18} className="text-blue-600" />
              <span className="font-medium text-gray-700">
                Status Center
              </span>
            </div>

            {/* Enhanced Status Popup */}
            {showStatus && (
              <div className="absolute right-0 top-12 w-64 bg-white shadow-xl border border-gray-200 rounded-xl p-4 text-sm z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                {statusCardPayload?.error ? (
                  <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg">
                    <XCircle size={18} />
                    <span>{statusCardPayload.error}</span>
                  </div>
                ) : !statusCardPayload ? (
                  <div className="flex items-center space-x-2 text-gray-600 bg-gray-50 p-3 rounded-lg">
                    <Loader size={18} className="animate-spin text-blue-600" />
                    <span>Loading analysis status...</span>
                  </div>
                ) : (
                  <>
                    <div className="mb-4 flex items-start justify-between gap-3 border-b border-gray-200 pb-3">
                      <div className="font-semibold text-gray-800">
                        Analysis Status
                        <div className="text-xs text-gray-500 font-normal mt-1">Last 1 Hour</div>
                      </div>
                      {statusLoading && (
                        <Loader size={16} className="mt-1 animate-spin text-blue-600" />
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      {/* In Progress */}
                      <div className="flex items-center justify-between p-2 rounded-lg hover:bg-amber-50 transition-colors">
                        <div className="flex items-center space-x-2">
                          <Loader size={16} className="text-amber-600 animate-spin" />
                          <span className="text-gray-700">In Progress</span>
                        </div>
                        <span className="font-semibold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full text-xs">
                          {statusCardPayload.in_progress?.length || 0}
                        </span>
                      </div>

                      {/* Failed */}
                      <div className="flex items-center justify-between p-2 rounded-lg hover:bg-red-50 transition-colors">
                        <div className="flex items-center space-x-2">
                          <XCircle size={16} className="text-red-600" />
                          <span className="text-gray-700">Failed</span>
                        </div>
                        <span className="font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full text-xs">
                          {statusCardPayload.failed?.length || 0}
                        </span>
                      </div>

                      {/* Succeeded */}
                      <div className="flex items-center justify-between p-2 rounded-lg hover:bg-green-50 transition-colors">
                        <div className="flex items-center space-x-2">
                          <CheckCircle size={16} className="text-green-600" />
                          <span className="text-gray-700">Succeeded</span>
                        </div>
                        <span className="font-semibold text-green-600 bg-green-100 px-2 py-0.5 rounded-full text-xs">
                          {statusCardPayload.succeeded?.length || 0}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Logout button */}
        <button
          className="flex items-center space-x-1 text-gray-700 hover:text-red-600 transition-colors"
          onClick={onLogout}
          title="Logout"
        >
          <LogOut size={18} />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
};

export default TopNav;
